import { useState, type ChangeEvent } from "react";
import { apiFetch, ApiError } from "../api/client";

/**
 * Bulk-import questions from a Markdown file into an existing test.
 *
 *   1. Admin pastes or uploads a .md file
 *   2. Click "Preview" → backend parses with dry_run=true, response shows the
 *      parsed questions + any per-question errors
 *   3. If clean, click "Import" → backend creates the questions atomically
 *      and the parent refetches the test
 */

interface ParsedOption {
  text: string;
  is_correct: boolean;
}
interface ParsedQuestion {
  type: "mcq" | "free_text";
  prompt: string;
  points: number;
  position: number;
  title: string | null;
  options: ParsedOption[];
}
interface ParseError {
  question_index: number | null;
  message: string;
}
interface PreviewResponse {
  parsed: ParsedQuestion[];
  errors: ParseError[];
}

const SAMPLE = `# Example import

## Question 1
Type: mcq
Title: Dockerfile basics
Points: 2

Prompt:
Which file is used to define how a Docker image is built?

Options:
- [x] Dockerfile
- [ ] docker-compose.yml
- [ ] package.json

## Question 2
Type: text
Points: 5

Prompt:
Explain the difference between an image and a container.
`;

export function MarkdownImportSection({
  testId,
  onImported,
}: {
  testId: string;
  onImported: () => void;
}) {
  const [markdown, setMarkdown] = useState("");
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imported, setImported] = useState<number | null>(null);

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    try {
      const text = await file.text();
      setMarkdown(text);
      setPreview(null);
      setImported(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function doPreview() {
    if (!markdown.trim()) {
      setError("Paste or upload some Markdown first.");
      return;
    }
    setBusy(true);
    setError(null);
    setImported(null);
    try {
      const res = await apiFetch<PreviewResponse>(
        `/admin/tests/${testId}/questions/import-markdown`,
        {
          method: "POST",
          body: JSON.stringify({ markdown, dry_run: true }),
        },
      );
      setPreview(res);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function doImport() {
    if (!markdown.trim()) return;
    setBusy(true);
    setError(null);
    setImported(null);
    try {
      const res = await apiFetch<{
        created: Array<{ id: string }>;
        parsed: ParsedQuestion[];
      }>(`/admin/tests/${testId}/questions/import-markdown`, {
        method: "POST",
        body: JSON.stringify({ markdown }),
      });
      setImported(res.created.length);
      setPreview(null);
      setMarkdown("");
      onImported();
    } catch (err) {
      // Backend returns 400 with the parsed + errors blob when the markdown
      // is invalid. Surface that as a preview so the admin can see what's
      // wrong (instead of an opaque message).
      if (err instanceof ApiError && err.status === 400 && err.body) {
        const body = err.body as Partial<PreviewResponse>;
        if (Array.isArray(body.errors)) {
          setPreview({
            parsed: body.parsed ?? [],
            errors: body.errors,
          });
          setError(
            `Cannot import — ${body.errors.length} error(s); see preview below.`,
          );
          return;
        }
      }
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  // Import is enabled whenever the admin has something to import (and we're
  // not mid-request). If the backend rejects the parse, errors land in the
  // preview block — no need to force the admin to click Preview first.
  const canImport = markdown.trim().length > 0 && !busy;

  return (
    <section className="md-import">
      <h2>Import questions from Markdown</h2>
      <p>
        <small>
          Append parsed questions to this test. Existing questions are not
          touched. Use the format documented below (Type / Title / Points /
          Prompt / Options).
        </small>
      </p>

      {error && (
        <div className="error-banner" role="alert">
          {error}
        </div>
      )}
      {imported !== null && (
        <div className="credentials-banner" role="status">
          Imported {imported} question{imported === 1 ? "" : "s"}.
        </div>
      )}

      <div className="md-import-controls">
        <label className="field-block">
          Upload .md file
          <input
            type="file"
            accept=".md,text/markdown,text/plain"
            onChange={(e) => void handleFile(e)}
            disabled={busy}
          />
        </label>
        <label className="field-block">
          Or paste Markdown
          <textarea
            className="admin-textarea md-import-textarea"
            value={markdown}
            onChange={(e) => {
              setMarkdown(e.target.value);
              setPreview(null);
              setImported(null);
            }}
            placeholder={SAMPLE}
          />
        </label>
        <div className="actions">
          <button
            type="button"
            onClick={() => void doPreview()}
            disabled={busy || !markdown.trim()}
          >
            {busy ? "Working…" : "Preview"}
          </button>{" "}
          <button
            type="button"
            onClick={() => void doImport()}
            disabled={!canImport}
          >
            {preview && preview.errors.length === 0 && preview.parsed.length > 0
              ? `Import ${preview.parsed.length} question${preview.parsed.length === 1 ? "" : "s"}`
              : "Import"}
          </button>
        </div>
      </div>

      {preview && <PreviewBlock preview={preview} />}
    </section>
  );
}

function PreviewBlock({ preview }: { preview: PreviewResponse }) {
  return (
    <div className="md-import-preview">
      <h3>Preview</h3>
      {preview.errors.length > 0 && (
        <div className="error-banner" role="alert">
          <strong>Errors — fix these before importing:</strong>
          <ul>
            {preview.errors.map((e, i) => (
              <li key={i}>
                {e.question_index !== null
                  ? `Question ${e.question_index}: `
                  : ""}
                {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {preview.parsed.length === 0 ? (
        <p>
          <em>No questions parsed.</em>
        </p>
      ) : (
        <ol>
          {preview.parsed.map((q) => (
            <li key={q.position}>
              <strong>{q.prompt.split("\n")[0]}</strong>{" "}
              <small>
                ({q.type}, {q.points} pts
                {q.title ? ` · "${q.title}"` : ""})
              </small>
              {q.type === "mcq" && (
                <ul>
                  {q.options.map((o, i) => (
                    <li key={i}>
                      {o.is_correct ? "✓ " : "  "}
                      {o.text}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
